import React, { Component } from "react";
import TemplatePage from "./TemplatePage";
import TemplatePreview from "./TemplatePreview";
import withStoreTemplates from "./withStoreTemplates";
import { isMobileApp } from "../../../actions/Util";
import {
  GALLERY,
  applyDesignCss,
  getModule,
  getPreviewHeight,
  getStartingConfig,
  getThumbHeight,
  getTransactionType,
  groupBySize,
  isSameModule,
  renderTemplate,
  sampleData,
  storeLocation
} from "./templateUtils";

class PrintTemplateGallery extends Component {
  constructor(props) {
    super(props);
    this.state = { designs: [], selectedId: null, loading: true };
  }

  get typeKey() {
    return this.props.match.params.typeKey;
  }

  async componentDidMount() {
    const type = getTransactionType(this.props, this.typeKey);
    const module = getModule(type);
    const designs = this.getStoreDesigns(module);

    // The built-in designs only have Sale fields, so an Inventory document
    // would print empty on them
    if (module === "sales") {
      designs.unshift(...(await this.getBuiltInDesigns()));
    }
    // Back from Customize: the design just tried. From Add: the size in use.
    const { size, selectedId } = this.props.location.state || {};
    const first =
      designs.find((d) => d.id === selectedId) ||
      designs.find((d) => d.size === size) ||
      designs[0];
    this.setState({
      designs,
      selectedId: first ? first.id : null,
      loading: false
    });
  }

  async getBuiltInDesigns() {
    const sources = {};
    await Promise.all(
      [...new Set(GALLERY.map((d) => d.source))].map(async (source) => {
        const record = await this.props.getPrintTemplate(source);
        sources[source] = record ? record.PrintTemplate : null;
      })
    );
    return GALLERY.filter((d) => sources[d.source]).map((d) => ({
      id: d.id,
      name: `${d.size} ${d.name}`,
      size: d.size,
      template: applyDesignCss(sources[d.source], d.css)
    }));
  }

  // Templates the store uses on its other documents of the same module
  getStoreDesigns(module) {
    return this.props.printConfigs.filter(
      (c) =>
        !c.IsDeleted &&
        c.PrintTemplate &&
        c.TransactionTypeID !== this.typeKey &&
        isSameModule(c, module)
    ).map((c) => ({
      id: c.key,
      name: c.PrintDisplayName,
      size: c.PrintSize,
      template: c.PrintTemplate
    }));
  }

  renderHtml(design) {
    const config = getStartingConfig(this.props, this.typeKey, design.size);
    return renderTemplate(design.template, sampleData(config, this.props.store));
  }

  onUseThis = (design) => {
    const { history, location, storeKey } = this.props;
    const { replaceKey } = location.state || {};
    history.replace({
      ...location,
      state: { ...location.state, selectedId: design.id }
    });
    history.push(
      storeLocation(storeKey, `/${this.typeKey}/customize/${replaceKey || "new"}`, {
        starter: design
      })
    );
    if (replaceKey) {
      this.props.getToastr("Design changed. Tap Save Template to keep it.");
    }
  };

  renderThumb(design) {
    return (
      <button
        key={design.id}
        type="button"
        className={"pt-thumb" + (design.id === this.state.selectedId ? " is-selected" : "")}
        onClick={() => this.setState({ selectedId: design.id })}
      >
        <span className="pt-thumb-page">
          <TemplatePreview
            html={this.renderHtml(design)}
            size={design.size}
            maxHeight={getThumbHeight()}
          />
        </span>
        <span className="pt-thumb-name" title={design.name}>{design.name}</span>
      </button>
    );
  }

  render() {
    const { designs, selectedId, loading } = this.state;
    const selected = designs.find((d) => d.id === selectedId);
    const type = getTransactionType(this.props, this.typeKey);

    return (
      <TemplatePage
        {...this.props}
        showBack
        onBack={() => this.props.history.goBack()}
        title="Choose a template"
        subtitle={type ? type.TransactionType : ""}
      >
        {loading && <div className="pt-empty pt-empty--loading">Loading templates...</div>}
        {!loading && !selected && (
          <div className="pt-empty">
            No templates are available for this document yet.
          </div>
        )}
        {selected && (
          <React.Fragment>
            <div className="pt-workspace">
              <div className="pt-canvas">
                <div className="pt-canvas-label">{selected.name}</div>
                <TemplatePreview
                  key={selected.id}
                  html={this.renderHtml(selected)}
                  size={selected.size}
                  maxHeight={getPreviewHeight()}
                  trueSize={!isMobileApp()}
                />
                <div className="pt-sample-note">Preview uses sample items</div>
              </div>
              <aside className="pt-panel">
                {groupBySize(designs, (d) => d.size)
                  .filter((group) => group.items.length > 0)
                  .map((group) => (
                    <div key={group.size} className="pt-size-group">
                      <div className="pt-section-title">{group.size}</div>
                      <div className="pt-thumbs">
                        {group.items.map((d) => this.renderThumb(d))}
                      </div>
                    </div>
                  ))}
              </aside>
            </div>
            <div className="pt-bar">
              <button
                type="button"
                className="pt-btn pt-btn--primary"
                onClick={() => this.onUseThis(selected)}
              >
                Use this template
              </button>
            </div>
          </React.Fragment>
        )}
      </TemplatePage>
    );
  }
}

export default withStoreTemplates(PrintTemplateGallery);
